import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import routes from "../../../../routes/routes";
import { useKeyHandoverStepCounter } from "../hooks/useKeyHandoverStepCounter";
import KeyHandoverHostDetailsStep from "../steps/KeyHandoverHostDetailsStep";
import KeyHandoverNavigateNextAction from "../common/util/KeyHandoverNavigateNextAction";
import { useLocationHydration } from "../hooks/useLocationHydration";
import { useServiceHydration } from "../hooks/useServiceHydration";
import { useHostDetailsHydration } from "../hooks/useHostDetailsHydration";
import { useHostDetailsUrlSync } from "../hooks/useHostDetailsUrlSync";
import type { RootState } from "../../../../redux/store";
import { useKeyHandoverPageInit } from "../hooks/useKeyHandoverPageInit";
import { KeyHandoverPageLayout } from "../components/KeyHandoverPageLayout";
import { KeyHandoverContentWrapper } from "../components/KeyHandoverContentWrapper";
import { Spinner } from "../../../../components/ui/spinner";

/**
 * Content component that handles all hydration and URL synchronization.
 */
const HostDetailsContent: React.FC = () => {
  // Hydrate location and service from URL
  const locationHydrated = useLocationHydration();
  useServiceHydration();

  // Hydrate host details from URL
  const hostDetailsHydrated = useHostDetailsHydration();

  // Sync host details changes back to URL
  useHostDetailsUrlSync();

  // Show loading spinner while critical hydration is in progress
  if (!locationHydrated || !hostDetailsHydrated) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner />
      </div>
    );
  }

  return <KeyHandoverHostDetailsStep />;
};

/**
 * KeyHandoverHostDetailsPage Component
 * ---------------------------------------------------------------
 * Manages the host details collection step in the key handover booking flow.
 */
const KeyHandoverHostDetailsPage: React.FC = () => {
  const { t } = useTranslation();

  // Redux selectors
  const { isCombined } = useKeyHandoverStepCounter("host");
  const selectedGroup = useSelector((s: RootState) => s.bookingCreation.selectedGroup);
  const landlordDataValidation = useSelector((s: RootState) => s.bookingCreation.landlordDataValidation);

  // Initialize page
  useKeyHandoverPageInit();

  // Derive navigation and validation state
  const backTo = isCombined ? routes.keyHandoverReturnSchedulePath : routes.keyHandoverSchedulePath;
  const isNextEnabled = Boolean(
    selectedGroup &&
    landlordDataValidation.firstName &&
    landlordDataValidation.email &&
    landlordDataValidation.phone
  );

  const bottomActions = selectedGroup ? (
    <div className="w-full">
      <KeyHandoverNavigateNextAction
        to={routes.keyHandoverGuestDetailsPath}
        enabled={isNextEnabled}
        bounceKey={isNextEnabled ? selectedGroup?.id ?? null : null}
      />
    </div>
  ) : undefined;

  return (
    <KeyHandoverPageLayout
      headerText={t("book_location.keys.host_details.header_text", {
        defaultValue: "Host Details",
      })}
      backTo={backTo}
      bottomActions={bottomActions}
    >
      <KeyHandoverContentWrapper>
        <HostDetailsContent />
      </KeyHandoverContentWrapper>
    </KeyHandoverPageLayout>
  );
};

export default KeyHandoverHostDetailsPage;
